import React from "react";
import {observer} from "mobx-react-lite";
import {Box, Stack, Typography} from "@mui/material";
import {useStores} from "~/stores";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {toCurrencyWithP} from "~/components/currency-component/CurrencyClasses";
import type {GeoSpendPoint} from "~/components/visualization/geo-spend-map/GeoSpendMap";
import {GEO_SPEND_BUBBLE_COLOR, GeoSpendMap} from "~/components/visualization/geo-spend-map/GeoSpendMap";
import {ChartCard} from "./ChartCard";
import {sizeLegendValues} from "./chartHelpers";

const LEGEND_DOT_SIZES = [6, 10, 14];

/**
 * Supplier locations map (the Category Profile's map), by country or (when configured) by region.
 * Clicking a bubble filters on it.
 */
export const SupplierLocationsCard: React.FC = observer(() => {
    const {p, bagStore} = useStores();
    const manager = bagStore.advanced;
    const byRegion = manager.hasGeoRegionFilter;
    const money = (v: number) => toCurrencyWithP(v, p.currencyFormat, p.currencySymbol);

    const data: GeoSpendPoint[] = byRegion
        ? manager.geoRegionBubbleData.map(d => ({
            key: d.id,
            spend: d.size,
            label: d.data.region_long || 'Unknown',
            lonLat: d.point,
        }))
        : manager.spendCountryData.map(d => ({key: d.country, spend: d.spend}));
    const regionSelection = manager.filters.geo_regions;
    const selected = byRegion
        ? (regionSelection?.inclusive ? regionSelection.selected.map(o => String(o.id)) : [])
        : (manager.filters.country ?? []);

    const legend = sizeLegendValues(data.map(d => d.spend));
    const dotSizes = LEGEND_DOT_SIZES.slice(-legend.length);

    return <ChartCard
        title="Supplier locations"
        subtitle={byRegion ? 'Spend by region' : 'Spend by country'}
        inlineSubtitle
        fill
        actions={legend.length > 0 && <Stack direction="row" alignItems="center" gap={1} className="spend-landscape-size-legend">
            {legend.map((v, i) => <Stack key={v} direction="row" alignItems="center" gap={0.5}>
                <Box className="spend-landscape-size-dot"
                     sx={{width: dotSizes[i], height: dotSizes[i], background: GEO_SPEND_BUBBLE_COLOR}}/>
                <Typography variant="caption" fontWeight={600}><CurrencyComponent v={v} nDigits={3}/></Typography>
            </Stack>)}
        </Stack>}>
        {/* Fills the card, so its bottom lines up with the treemap and legend next to it */}
        <Box sx={{flex: 1, minHeight: 360}}>
            <GeoSpendMap
            data={data}
            money={money}
            fill
            selected={selected}
            onSelectionChange={byRegion ? manager.geoRegionFilterChangedByBubbleMap : manager.countryFilterChanged}
            emptyText={byRegion ? 'No supplier regions in these filters.' : 'No supplier countries in these filters.'}
            />
        </Box>
    </ChartCard>;
});
